import type { MeetingKind } from './calendar.ts';

/**
 * One section of a template: a title and a description, nothing else. No
 * settings, no instructions, no logic — a template is content.
 */
export interface MeetingTemplateSection {
  title: string;
  description: string;
}

/**
 * The colours a gesprekstype can wear, as the design system's pill colours.
 *
 * A fixed palette rather than a colour wheel: every pill in the product has to
 * stay readable in light and dark, and a hex an organisation typed in cannot
 * promise that. Named after the colour rather than after the template that
 * happened to wear it first — the colour travels with a template of any name.
 */
export const MEETING_TEMPLATE_COLORS = [
  'navy',
  'green',
  'pink',
  'purple',
  'teal',
  'grey',
] as const;

export type MeetingTemplateColor = (typeof MEETING_TEMPLATE_COLORS)[number];

/** The colour a meeting with no gesprekstype at all wears. */
export const MEETING_TEMPLATE_FALLBACK_COLOR: MeetingTemplateColor = 'grey';

/**
 * Whose gesprekstype this is. An organisation template is everyone's to read
 * and an admin's to change; a personal one is its owner's alone, and nobody
 * else ever sees that it exists.
 */
export type MeetingTemplateScope = 'organisation' | 'personal';

/**
 * The one kind of meeting a gesprekstype is for. Chosen once, at creation, and
 * never changed: a type needed for two kinds is duplicated.
 */
export type MeetingTemplateKind = MeetingKind;

/**
 * Why a list of templates is read. `use` is a picker: what this user may put
 * on a meeting. `manage` is the settings list, where an admin keeps every
 * cohort's templates.
 */
export type MeetingTemplateListPurpose = 'use' | 'manage';

/** The track an organisation template is aimed at, named enough to label it. */
export interface MeetingTemplateTrack {
  id: string;
  title: string;
}

/** The cohort an organisation template is aimed at, named enough to label it. */
export interface MeetingTemplateCohort {
  id: string;
  title: string;
  /** The title with its track in brackets when it has one: what a badge shows. */
  label: string;
}

/**
 * A gesprekstype as a list row: what it is called, what colour it wears, whose
 * it is, which kind of meeting it is for, and whether this caller may change it.
 *
 * No sections. This is what the picker on a meeting page shows and what the
 * settings list shows — a structure is a document, and neither of them reads
 * one. The picker filters this list on `kind` itself.
 */
export interface MeetingTemplateItem {
  id: string;
  name: string;
  color: MeetingTemplateColor;
  scope: MeetingTemplateScope;
  kind: MeetingTemplateKind;
  /** Whether this caller may change or delete it, from their own standing. */
  editable: boolean;
  /**
   * Whether this is the gesprekstype the organisation's meetings of `kind`
   * follow when nobody picked one ("Default for team meetings"). At most one
   * per kind, and never a personal one.
   */
  is_default: boolean;
  /**
   * The cohort or track this organisation template is aimed at, never both;
   * both null is the whole organisation. A member is only ever handed their
   * own cohort's and its track's.
   */
  cohort: MeetingTemplateCohort | null;
  track: MeetingTemplateTrack | null;
}

/**
 * One gesprekstype in full: its row, what the type itself is about, plus the
 * structure it gives.
 *
 * `description` explains the type as a whole, above the topics — what kind of
 * conversation this is, in a sentence or two. Null on a template that never
 * said: most types speak for themselves through their name, and nothing is
 * written on their behalf.
 */
export interface MeetingTemplateDetail extends MeetingTemplateItem {
  description: string | null;
  sections: MeetingTemplateSection[];
}

/** The gesprekstypes this user may use: the organisation's, plus their own. */
export interface MeetingTemplatesResponse {
  data: MeetingTemplateItem[];
}

/**
 * What a new gesprekstype is made of. Scope and kind are chosen once, at
 * creation.
 *
 * `description` is optional everywhere it is written: absent, empty and
 * whitespace all mean the same thing, and all three are stored as nothing.
 */
export interface MeetingTemplateCreateRequest {
  name: string;
  color: MeetingTemplateColor;
  scope: MeetingTemplateScope;
  kind: MeetingTemplateKind;
  description?: string | null;
  sections: MeetingTemplateSection[];
  /**
   * Organisation templates only: one track or one cohort, never both. Absent
   * or null is the whole organisation.
   */
  track_id?: string | null;
  cohort_id?: string | null;
}

/**
 * What an existing gesprekstype is changed to. The whole template every time —
 * it is edited as one form and saved as one record, so there is nothing to
 * merge. Scope and kind are absent on purpose: neither changes once the
 * template exists.
 */
export interface MeetingTemplateUpdateRequest {
  name: string;
  color: MeetingTemplateColor;
  description?: string | null;
  sections: MeetingTemplateSection[];
  /**
   * Organisation templates only: setting one clears the other, null on both
   * aims it at the whole organisation again, absent leaves it as it is.
   */
  track_id?: string | null;
  cohort_id?: string | null;
}

/**
 * Which kind of meeting falls back to a template when nobody picked one —
 * always the template's own kind. Null means it is only ever chosen by hand.
 */
export type MeetingTemplateDefaultKind = MeetingTemplateKind | null;

/**
 * A gesprekstype as Wundo staff read and edit it: the whole record, structure
 * included.
 *
 * One shape for both lists the staff console keeps — Wundo's defaults and one
 * organisation's own templates — because they are the same thing at two
 * moments in its life: what an organisation will be handed, and what it holds.
 *
 * `kind` is set once, at creation. `default_kind` is shown rather than edited
 * here: on an organisation's template staff set it with the make-default
 * action, one per kind; on a Wundo default it is never set from the console.
 */
export interface MeetingTemplateRecord {
  id: string;
  name: string;
  color: MeetingTemplateColor;
  kind: MeetingTemplateKind;
  default_kind: MeetingTemplateDefaultKind;
  description: string | null;
  sections: MeetingTemplateSection[];
  /** The cohort or track it is aimed at. Always null on Wundo's defaults. */
  cohort: MeetingTemplateCohort | null;
  track: MeetingTemplateTrack | null;
}

/**
 * What a new Wundo default or organisation template is made of, as staff write
 * it. The kind is chosen here and never again.
 */
export interface MeetingTemplateRecordCreateRequest extends MeetingTemplateUpdateRequest {
  kind: MeetingTemplateKind;
}

/** Wundo's defaults, or one organisation's own templates. */
export interface MeetingTemplateRecordsResponse {
  data: MeetingTemplateRecord[];
}
